import { pmFetchJson } from "@penmate/react-shared/api/pmFetch";
import { contactName } from "../flow/names";
import type { Facility } from "@penmate/react-shared/api/facilities";
import type { Candidate } from "./searches";
import type { SearchDraft } from "../flow/types";

// The always-succeeds add: POST /api/my-penmates. The server dedupes, creates
// the contact, resolves a facility_id to a real mailing address (free-text
// facility strings are captured for the team instead), links the search via
// search_id, and returns the user's full penmate list.

export type Penmate = {
  id: number;
  slug: string | null;
  first_name: string | null;
  last_name: string | null;
  inmate_id: string | null;
  facility: string | null;
  state: string | null;
};

// Search linkage for the server-side add-penmate analytics event — event
// payload only, never contact data.
export type SearchMeta = {
  matched: boolean;
  from_source: string | null;
  confirmed_facility: string | null;
  // The settled wait's birthday ask: `offered` = the disclosure rendered
  // (no birthday was known), `late_birthdate` = it was filled. Together they
  // give the ask's fill rate; who-step birthdays set neither.
  late_birthdate?: boolean;
  late_birthdate_offered?: boolean;
  // Unmatched facility text travelled with the add as a review hint.
  facility_hint_present?: boolean;
};

type AddPenmateBody = {
  penmate: {
    birthdate?: string;
    first_name: string;
    last_name: string;
    state: string;
    inmate_id?: string;
    facility?: string;
    facility_id?: number;
    // What the user typed for the facility when nothing matched it — kept on
    // the contact's record for review, never shown to the user. ≤120 chars.
    facility_hint?: string;
  };
  search_id?: number;
  search_meta?: SearchMeta;
  metrics_context?: Record<string, unknown>;
};

const buildBody = (args: {
  draft: SearchDraft;
  candidate?: Candidate | null;
  knownFacility?: Facility | null;
  facilityHint?: string;
  // The typeahead found nothing for the hint: only then may it stand as the
  // contact's free-text facility.
  facilityHintUnmatched?: boolean;
  searchId?: number | null;
  metricsContext?: Record<string, unknown>;
  searchMeta?: SearchMeta;
  lateBirthdate?: string | null;
}): AddPenmateBody => {
  const { draft, candidate, knownFacility, searchId, metricsContext, searchMeta } = args;
  const facilityHint = (args.facilityHint || "").trim().slice(0, 120);
  // A confirmed record names the contact (that is the name the mailroom
  // matches); the typed name was the search key and stands only without one.
  const name = contactName(draft, candidate);
  const body: AddPenmateBody = {
    penmate: {
      first_name: name.first,
      last_name: name.last,
      state: draft.mode === "federal" ? "FEDERAL" : draft.state || "",
    },
  };
  // A birthday helps whoever verifies the contact later; the server stores it
  // best-effort on the contact's record and never errors over it.
  const birthdate = args.lateBirthdate || draft.birthdate;
  if (birthdate) body.penmate.birthdate = birthdate;
  // The server dedupes with ILIKE wildcards — strip pattern characters so a
  // typed % or _ can't bind the add to an arbitrary existing contact.
  const rawId = (candidate && candidate.inmateId) || draft.inmateId;
  const inmateId = rawId ? rawId.replace(/[%_]/g, "") : "";
  if (inmateId) body.penmate.inmate_id = inmateId;
  // A confirmed candidate's facility wins over the step-2 pick: the server
  // stamps a facility_id's mailing address onto the contact, and the found
  // person may be somewhere other than where the user guessed. The step-2
  // facility_id rides only when there is no candidate — with its name
  // alongside, so an unresolvable id still leaves a usable facility string.
  if (candidate) {
    if (candidate.facility) body.penmate.facility = candidate.facility;
  } else if (knownFacility) {
    body.penmate.facility_id = knownFacility.id;
    if (knownFacility.name) body.penmate.facility = knownFacility.name;
  } else if (facilityHint && args.facilityHintUnmatched) {
    // Nothing else places the contact and the typeahead had nothing for the
    // text: it stands as the free-text facility for review (never a new
    // facility record). A prefix that had results showing does not.
    body.penmate.facility = facilityHint;
  }
  if (facilityHint) body.penmate.facility_hint = facilityHint;
  if (searchId != null) body.search_id = searchId;
  if (searchMeta) body.search_meta = searchMeta;
  // Session attribution rides the same POST so the server's add-penmate event
  // carries it (parity with the legacy Angular add path).
  if (metricsContext && Object.keys(metricsContext).length > 0) {
    body.metrics_context = metricsContext;
  }
  return body;
};

// Best-effort match of the created/deduped contact in the returned list, for
// the handoff's compose CTA.
const findAdded = (penmates: Penmate[], body: AddPenmateBody): Penmate | null => {
  const wantId = (body.penmate.inmate_id || "").toLowerCase();
  if (wantId) {
    const byId = penmates.find((p) => (p.inmate_id || "").toLowerCase() === wantId);
    if (byId) return byId;
  }
  const first = body.penmate.first_name.toLowerCase();
  const last = body.penmate.last_name.toLowerCase();
  return (
    penmates.find(
      (p) =>
        (p.first_name || "").toLowerCase() === first &&
        (p.last_name || "").toLowerCase() === last,
    ) || null
  );
};

export async function addPenmate(args: {
  draft: SearchDraft;
  candidate?: Candidate | null;
  knownFacility?: Facility | null;
  facilityHint?: string;
  facilityHintUnmatched?: boolean;
  searchId?: number | null;
  metricsContext?: Record<string, unknown>;
  searchMeta?: SearchMeta;
  lateBirthdate?: string | null;
}): Promise<Penmate | null> {
  const body = buildBody(args);
  const res = await pmFetchJson<{ penmates: Penmate[] }>("/my-penmates/", {
    method: "POST",
    body,
  });
  const penmates = Array.isArray(res.penmates) ? res.penmates : [];
  return findAdded(penmates, body);
}
